import { Injectable } from '@angular/core';

/**
 * Container formats a browser will record into, best first. No browser supports
 * all of them: Chrome and Firefox record WebM/Opus, Safari records MP4/AAC, so
 * whichever comes back depends on where the recording was made.
 */
const PREFERRED_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
  'audio/ogg',
];

/** Extension to give the recording, keyed by the container it came out as. */
const EXTENSIONS: Record<string, string> = {
  'audio/webm': 'webm',
  'audio/mp4': 'm4a',
  'audio/ogg': 'ogg',
  'audio/mpeg': 'mp3',
};

/**
 * Records from the microphone and hands back a File ready to upload.
 *
 * The mic stays open for as long as a recording is running, so every exit path
 * — stop, cancel, failure — has to release it or the browser keeps showing the
 * tab as recording.
 */
@Injectable({
  providedIn: 'root',
})
export class AudioRecorderService {
  private recorder?: MediaRecorder;
  private stream?: MediaStream;
  private chunks: Blob[] = [];

  /** getUserMedia needs a secure context, so this is false on plain HTTP. */
  get isSupported(): boolean {
    return (
      typeof MediaRecorder !== 'undefined' &&
      !!navigator.mediaDevices?.getUserMedia
    );
  }

  /** Prompts for the microphone on first use, and rejects if it is refused. */
  async start(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    const mimeType = PREFERRED_MIME_TYPES.find((type) =>
      MediaRecorder.isTypeSupported(type),
    );

    this.chunks = [];
    this.recorder = new MediaRecorder(
      this.stream,
      mimeType ? { mimeType } : undefined,
    );

    this.recorder.ondataavailable = (event) => {
      if (event.data.size) {
        this.chunks.push(event.data);
      }
    };

    this.recorder.start();
  }

  stop(): Promise<File> {
    return new Promise((resolve, reject) => {
      const recorder = this.recorder;

      if (!recorder) {
        reject(new Error('No recording in progress.'));
        return;
      }

      recorder.onstop = () => {
        // mimeType carries the codec too ("audio/webm;codecs=opus"); the file
        // needs the bare container so it matches the accepted types.
        const container = recorder.mimeType.split(';')[0] || 'audio/webm';
        const extension = EXTENSIONS[container] ?? 'webm';
        const blob = new Blob(this.chunks, { type: container });

        this.release();

        resolve(
          new File([blob], `voice-${Date.now()}.${extension}`, {
            type: container,
          }),
        );
      };

      recorder.stop();
    });
  }

  cancel(): void {
    if (this.recorder?.state === 'recording') {
      // Drop the handler first: stop() fires onstop, and there is nobody left
      // waiting for the file.
      this.recorder.onstop = null;
      this.recorder.stop();
    }

    this.release();
  }

  private release(): void {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = undefined;
    this.recorder = undefined;
    this.chunks = [];
  }
}
